import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  GraduationCap, 
  Clock, 
  Users, 
  Search,
  ArrowRight,
  BookOpen,
  Filter
} from "lucide-react";

const ExploreCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [settings, setSettings] = useState(null);
  const [pageSettings, setPageSettings] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, settingsRes, pageSettingsRes] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/courses-page-settings`)
      ]);
      setCourses(coursesRes.data);
      setSettings(settingsRes.data);
      setPageSettings(pageSettingsRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = settings?.primary_color || "#6d28d9";
  
  // Get unique categories
  const categories = [...new Set(courses.map(c => c.category).filter(Boolean))];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = 
      course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.short_description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || course.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: pageSettings?.background_color || "#f5f5f4" }}
    >
      {/* Header */}
      <header className="sticky top-0 z-30 w-full bg-white/80 backdrop-blur-xl border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-violet-700" />
            <span className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
              {settings?.site_name || 'LearnHub'}
            </span>
          </Link>
          <Link to="/">
            <Button variant="outline" className="rounded-full">
              Sign In
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <div 
        className="relative py-16 md:py-24"
        style={{ 
          background: pageSettings?.background_image 
            ? `url(${pageSettings.background_image.startsWith('http') ? pageSettings.background_image : API.replace('/api', '') + pageSettings.background_image}) center/cover`
            : `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}05 100%)`
        }}
      >
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 mb-6 leading-tight" 
            style={{ fontFamily: 'Outfit' }}
          >
            {pageSettings?.page_title || 'Our Courses'}
          </h1>
          <p className="text-lg md:text-xl text-stone-600 max-w-3xl mx-auto mb-8">
            {pageSettings?.page_subtitle || 'Discover your path to success'}
          </p>
          {pageSettings?.page_description && (
            <p className="text-stone-500 max-w-2xl mx-auto">
              {pageSettings.page_description}
            </p>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
            <Input
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-12 rounded-full bg-white border-stone-200"
              data-testid="search-courses-input"
            />
          </div>
          {(pageSettings?.show_categories !== false) && categories.length > 0 && (
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48 h-12 rounded-full bg-white" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Results Count */}
        <p className="text-stone-500 mb-6">
          Showing {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''}
        </p>

        {/* Courses Grid */}
        {filteredCourses.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">No courses found</h3>
            <p className="text-stone-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <Link 
                key={course.course_id} 
                to={`/course/${course.course_id}`}
                data-testid={`course-card-${course.course_id}`}
              >
                <Card className="bg-white rounded-2xl border border-stone-100 overflow-hidden hover:shadow-xl hover:border-violet-200 transition-all duration-300 h-full group">
                  {/* Thumbnail */}
                  <div className="aspect-video bg-gradient-to-br from-violet-500 to-purple-600 relative overflow-hidden">
                    {course.thumbnail ? (
                      <img 
                        src={course.thumbnail.startsWith('http') ? course.thumbnail : `${API.replace('/api', '')}${course.thumbnail}`}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="h-16 w-16 text-white/50" />
                      </div>
                    )}
                    {/* Category Badge */}
                    <Badge className="absolute top-4 left-4 bg-white/90 text-violet-700 backdrop-blur-sm">
                      {course.category}
                    </Badge>
                  </div>
                  
                  <CardContent className="p-6">
                    {/* Title */}
                    <h3 className="text-xl font-semibold text-stone-900 mb-2 line-clamp-2 group-hover:text-violet-700 transition-colors" style={{ fontFamily: 'Outfit' }}>
                      {course.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-stone-500 text-sm mb-4 line-clamp-2">
                      {course.short_description || course.description}
                    </p>
                    
                    {/* Meta Info */}
                    <div className="flex items-center gap-4 text-sm text-stone-500 mb-4">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{course.duration || '8 weeks'}</span>
                      </div>
                      {(pageSettings?.show_enrollment_count !== false) && (
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          <span>{course.enrolled_count || 0} enrolled</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Price & CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                      {(pageSettings?.show_prices !== false) && (
                        <p className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                          {course.price > 0 ? `£${course.price}` : 'Free'}
                        </p>
                      )}
                      <Button variant="ghost" className="text-violet-700 hover:text-violet-800 group-hover:translate-x-1 transition-transform">
                        View Course <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-300 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <GraduationCap className="h-6 w-6 text-violet-400" />
            <span className="font-bold text-white">{settings?.site_name || 'LearnHub'}</span>
          </div>
          <p className="text-sm">{settings?.footer_text || '© 2024 LearnHub. All rights reserved.'}</p>
        </div>
      </footer>
    </div>
  );
};

export default ExploreCourses;
